import React from "react";
import { DateTime } from "luxon";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Background } from "../../BuildingBlocks/Background";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { Text } from "../../BuildingBlocks/Text";
import { WeightBold } from "../../WeightBold/WeightBold";
import { THexColor } from "../../../models/StringLiterals";

const ACKNOWLEDGEMENT_DAYS = 7;
const FINAL_RESPONSE_DAYS = 28;

type TDeadline = {
    dueOn: DateTime;
    metOn: DateTime | null;
    isBreached: boolean;
};

type TCRMComplaintSlaBannerProps = {
    raisedAt: string | null;
    acknowledgedAt: string | null;
    resolvedAt: string | null;
};

export const CRMComplaintSlaBanner = (props: React.PropsWithChildren<TCRMComplaintSlaBannerProps>): JSX.Element => {

    if (props.raisedAt === null) {
        return <></>;
    }

    const toUtcDay = (iso: string): DateTime => DateTime.fromISO(iso, { zone: "utc" }).startOf("day");

    const raisedOn = toUtcDay(props.raisedAt);

    const getDeadline = (days: number, metAt: string | null): TDeadline => {
        const dueOn = raisedOn.plus({ days });
        const metOn = metAt === null ? null : toUtcDay(metAt);
        return {
            dueOn,
            metOn,
            isBreached: (metOn ?? DateTime.utc().startOf("day")) > dueOn,
        };
    };

    const acknowledgement = getDeadline(ACKNOWLEDGEMENT_DAYS, props.acknowledgedAt);
    const finalResponse = getDeadline(FINAL_RESPONSE_DAYS, props.resolvedAt);

    const isAnythingBreached = acknowledgement.isBreached || finalResponse.isBreached;
    const isEverythingDone = acknowledgement.metOn !== null && finalResponse.metOn !== null;

    const getColors = (): { background: THexColor, border: THexColor } => {
        if (isAnythingBreached) {
            return { background: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_12, border: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_10 };
        }
        if (isEverythingDone) {
            return { background: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_12, border: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_10 };
        }
        return { background: CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_12, border: CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_10 };
    };

    const formatDate = (date: DateTime): string => date.toFormat("d LLL yyyy");

    const renderDeadline = (label: string, deadline: TDeadline): JSX.Element => {
        const body = deadline.metOn !== null
            ? deadline.isBreached
                ? <>{label} <WeightBold>sent on {formatDate(deadline.metOn)}</WeightBold>, <WeightBold>past the {formatDate(deadline.dueOn)} deadline</WeightBold>.</>
                : <>{label} <WeightBold>sent on {formatDate(deadline.metOn)}</WeightBold>, <WeightBold>within the deadline</WeightBold>.</>
            : deadline.isBreached
                ? <>{label} is <WeightBold>overdue</WeightBold> — it was <WeightBold>due on {formatDate(deadline.dueOn)}</WeightBold>.</>
                : <>{label} <WeightBold>due by {formatDate(deadline.dueOn)}</WeightBold>.</>;

        return deadline.isBreached
            ? <Text color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}><CRMParagraph>{body}</CRMParagraph></Text>
            : <CRMParagraph>{body}</CRMParagraph>;
    };

    const colors = getColors();

    return (
        <Background padding={CRMSpacing.MEDIUM}>
            <Background
                color={colors.background}
                borderDefinition={`1px solid ${colors.border}`}
                borderRadius="5px"
                padding={CRMSpacing.MEDIUM}
            >
                <SpacingColumn spacing={CRMSpacing.SMALL}>
                    <WeightBold>Complaint deadlines</WeightBold>

                    <CRMParagraph>
                        Raised on <WeightBold>{formatDate(raisedOn)}</WeightBold>.
                    </CRMParagraph>

                    {renderDeadline("Acknowledgement", acknowledgement)}
                    {renderDeadline("Final written response", finalResponse)}
                </SpacingColumn>
            </Background>
        </Background>
    );
};
